/* eslint-disable react/no-array-index-key */
/* eslint-disable import/no-unresolved */
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { useStaticQuery, graphql } from 'gatsby'
import { motion } from 'framer-motion'
import { useMedia } from 'react-use'

// Components
import CustomLink from 'components/shared/CustomLink'

// SVG
import Logo from 'img/logo.inline.svg'
import ArrowRight from 'img/arrow-right.svg'
import ButtonOutline from 'components/elements/ButtonOutline'
import ButtonDefault from 'components/elements/ButtonDefault'

const StyledHeader = styled(motion.nav)`
  background-color: #faf1e7;
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  height: 90px;
  z-index: 3;

  @media (min-width: 2000px) {
    & .container-lg {
      max-width: 1600px;
    }
  }
`

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
`

const Brand = styled(CustomLink)`
  & > svg {
    width: 180px;
    height: auto;

    @media (max-width: 991px) {
      width: 140px;
      height: auto;
    }
  }
`

const NavButton = styled.button`
  width: 80px;
  height: 50px;
  padding: 0 10px;
  text-align: center;
  z-index: 10;
`

const NavButtonStripe = styled.div`
  background-color: ${(props) => props.theme.color.text.contrast};
  height: 2px;
  width: 70%;
  margin: 5px auto;
`

interface HeaderProps {
  setIsScrollable: Function
  isSticky?: boolean
}

function Header({ isSticky, setIsScrollable }: HeaderProps) {
  // eslint-disable-next-line
  const { fields } = useStaticQuery<GatsbyTypes.headerQueryQuery>(graphql`
    query headerQuery {
      fields: wpComponent(databaseId: { eq: 97 }) {
        header {
          menuHeader {
            link {
              title
              url
            }
            submenu {
              link {
                title
                url
              }
            }
          }
          buttons {
            link {
              title
              url
              target
            }
          }
        }
      }
    }
  `)

  return (
    <StyledHeader
      initial={{ boxShadow: 'none' }}
      animate={
        isSticky
          ? {
              borderBottom: '1px solid #C0B6A5',
            }
          : { borderBottom: 'none' }
      }
    >
      <Container className="container-fluid d-flex flex-row justify-content-between align-items-center">
        <div>
          <Brand to="/">
            <Logo />
          </Brand>
        </div>

        <div>
          <MenuMobile fields={fields} setIsScrollable={setIsScrollable} />
          <MenuDesktop fields={fields} />
        </div>
        <div
          className="d-none d-xl-flex flex-row justify-content-center flex-shrink-0 position-relative z-10 "
          style={{ gap: '12px' }}
        >
          {fields?.header?.buttons?.map((button: any, index: number) => {
            const item = button?.link
            if (!item?.title) return null
            const to = item.url || '/'
            if (index === 0) {
              return (
                <ButtonOutline
                  to={to}
                  key={`header-btn-${index}`}
                  target={item.target}
                >
                  {item.title}
                </ButtonOutline>
              )
            }
            return (
              <ButtonDefault
                to={to}
                key={`header-btn-${index}`}
                target={item.target}
              >
                {item.title}
              </ButtonDefault>
            )
          })}
        </div>
      </Container>
    </StyledHeader>
  )
}

const MenuWrapper = styled.div`
  background-color: ${(props) => props.theme.color.face.main};
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  z-index: 3;
  padding: 30px 0 0;

  & > ul {
    padding: 50px 0 0;
    list-style-type: none;
    text-align: center;
    @media (max-width: 991px) {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      width: 80% !important;
      margin: 0 auto;
    }

    & li {
      padding: 0;
      border-bottom: 1px solid #ded2c7;

      @media (max-width: 991px) {
        width: 100%;
        text-align: left;
      }

      & a {
        font-size: ${(props) => props.theme.font.size[24]};
        font-weight: 300;
        color: #241e1b !important;
        display: block;
        padding: 15px 0 !important;
      }
    }
  }
`

const NavButtonClose = styled.div`
  position: absolute;
  right: 20px;
  top: 20px;
  text-align: center;
`

const NavButtonCloseCross = styled.div`
  position: relative;
  width: 32px;
  height: 32px;
  margin-left: 16px;

  &:before,
  &:after {
    background-color: ${(props) => props.theme.color.text.contrast};
    position: absolute;
    content: ' ';
    height: 33px;
    width: 2px;
  }

  &:before {
    transform: rotate(45deg);
  }

  &:after {
    transform: rotate(-45deg);
  }
`

const MobileSubmenuOverlay = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  z-index: 4;
  background-color: ${(props) => props.theme.color.face.main};
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 0 2rem;
  display: flex;
  flex-direction: column;
`

const MobileSubmenuBackButton = styled.button`
  align-self: flex-start;
  margin: 20px 24px 16px;
  padding: 10px 0;
  border: none;
  background: transparent;
  font-family: ${(props) => props.theme.font.family.main};
  font-size: ${(props) => props.theme.font.size[18]};
  font-weight: ${(props) => props.theme.font.weight.m};
  color: ${(props) => props.theme.color.text.dark};
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const MobileSubmenuOverlayInner = styled.div`
  width: 80%;
  max-width: 520px;
  margin: 0 auto;
  padding-bottom: 2rem;
`

interface MenuMobileProps {
  fields: any
  setIsScrollable: Function
}

function MenuMobile({ fields, setIsScrollable }: MenuMobileProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const isBurgerMenu = useMedia('(max-width: 1199px)')

  return (
    <div className="d-block d-xl-none">
      <NavButton
        role="button"
        onClick={() => {
          setIsOpen(!isOpen)
          setIsScrollable(false)
        }}
      >
        <NavButtonStripe />
        <NavButtonStripe />
        <NavButtonStripe />
      </NavButton>
      {isOpen && (
        <MenuWrapper style={{ overflowY: 'scroll', overflowX: 'hidden' }}>
          <NavButtonClose
            role="button"
            onClick={() => {
              setIsOpen(!isOpen)
              setIsScrollable(true)
            }}
          >
            <NavButtonCloseCross />
          </NavButtonClose>
          <ul>
            <Menu fields={fields} drawerOpen={isOpen} />
          </ul>
          {isBurgerMenu && (
            <div>
              {fields?.header?.buttons?.map((button: any, index: number) => {
                const item = button?.link
                if (!item?.title) return null
                const to = item.url || '/'
                return (
                  <div
                    key={`header-btn-${index}`}
                    style={{ width: '80%', zIndex: 99 }}
                    className="d-flex justify-content-start justify-content-lg-center mx-auto position-relative mt-4"
                  >
                    {index === 0 ? (
                      <ButtonOutline
                        to={to}
                        target={item.target}
                        className="text-uppercase w-100"
                      >
                        {item.title}
                      </ButtonOutline>
                    ) : (
                      <ButtonDefault
                        to={to}
                        target={item.target}
                        className="text-uppercase w-100"
                      >
                        {item.title}
                      </ButtonDefault>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </MenuWrapper>
      )}
    </div>
  )
}

const StyledMenuDesktop = styled.ul`
  flex: 1;
  align-items: center;
  justify-content: space-around;
  list-style: none;
  margin-bottom: 0;
  gap: 34px;
`

interface MenuDesktopProps {
  fields: any
}

function MenuDesktop({ fields }: MenuDesktopProps) {
  return (
    <StyledMenuDesktop className="d-none d-xl-flex justify-content-end">
      <Menu fields={fields} />
    </StyledMenuDesktop>
  )
}

const Item = styled.li`
  font-family: ${(props) => props.theme.font.family.main};
  font-weight: ${(props) => props.theme.font.weight.s};
  & > a {
    text-transform: uppercase;
    color: ${(props) => props.theme.color.text.dark};
    letter-spacing: 1px;
    font-size: 14px;
    font-weight: 300;

    &[aria-current] {
      color: ${(props) => props.theme.color.text.light};
      font-weight: 400 !important;
      overflow: hidden !important;

      @media (min-width: 1200px) {
        color: ${(props) => props.theme.color.text.contrast};
      }
    }
  }

  & > .second-last-item {
    width: unset;
    font-size: 14px !important;

    & > a {
      cursor: pointer !important;
      font-size: 14px !important;
    }
  }
`

const LinkContainer = styled(motion.div)`
  & a {
    color: #000;
    font-weight: 300;
    font-size: 14px;

    &[aria-current] {
      color: ${(props) => props.theme.color.text.dark};
      overflow: hidden !important;
      @media (min-width: 1200px) {
        color: ${(props) => props.theme.color.text.contrast};
      }
    }
  }
`

const StyledCustomLink = styled(CustomLink)`
  position: relative;
  letter-spacing: 1px;
`

const HoverableItem = styled(motion.li)`
  font-family: ${(props) => props.theme.font.family.main};
  font-weight: ${(props) => props.theme.font.weight.s};

  & > a {
    text-transform: lowercase;
    color: ${(props) => props.theme.color.text.dark};
    letter-spacing: ${(props) => props.theme.font.spacing.s};

    &[aria-current] {
      color: ${(props) => props.theme.color.text.light};
      font-weight: 400 !important;
      overflow: hidden !important;
      @media (min-width: 1200px) {
        color: ${(props) => props.theme.color.face.contrast};
      }
    }
  }

  &:last-child {
    background-color: ${(props) => props.theme.color.face.light};
    transition: all 0.2s ease-in-out;

    & > a {
      border: 1px solid ${(props) => props.theme.color.text.main};
      padding: 0.75rem 1rem !important;
      border-radius: 5px;
    }

    &:before {
      visibility: hidden;
    }

    &:hover {
      background-color: ${(props) => props.theme.color.face.secondary};
      border: 1px solid ${(props) => props.theme.color.face.secondary};
    }

    &:after {
      content: url(${ArrowRight});
      padding-left: ${(props) => props.theme.font.spacing.l};
      @media (max-width: 575px) {
        content: ' ' !important;
      }
    }
  }

  @media (max-width: 991px) {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }
`

const SubMenuWrapper = styled(motion.div)`
  position: absolute;
  top: 90px;
  background: ${(props) => props.theme.color.text.light};
  max-width: 320px;
  height: auto;
  transition: all 0.2s ease-in-out;
  overflow: hidden;
`

const HoverableSubItem = styled(motion.div)`
  & a {
    text-transform: uppercase;

    &:hover {
      font-weight: ${(props) => props.theme.font.weight.m};
    }

    @media (max-width: 1200px) {
      font-family: ${(props) => props.theme.font.family.main};
      font-size: ${(props) => props.theme.font.size[24]} !important;
      font-weight: 300;
      color: #241e1b !important;
    }
  }
  @media (max-width: 991px) {
    padding-left: 0 !important;
    padding-right: 0 !important;
    width: 100%;
    border-bottom: 1px solid #f1f1f1;
    &:last-child {
      border: none;
    }
  }
`

const SubMenuCustomLink = styled(CustomLink)`
  @media (max-width: 991px) {
    font-size: 14px !important;
  }
`

interface SubMenuProps {
  fields: any
}

interface MenuProps {
  fields: any
  /** When the mobile drawer closes, submenu overlay resets */
  drawerOpen?: boolean
}

function SubMenu({ fields }: SubMenuProps) {
  return (
    <>
      {fields.submenu.map((edge: any, index: number) => (
        <HoverableSubItem
          className="col-12 d-flex justify-content-center mb-3"
          key={index}
        >
          <motion.div className="d-flex w-100 justify-content-start align-items-center py-2">
            <div>
              <SubMenuCustomLink to={edge.link.url}>
                {edge.link.title}
              </SubMenuCustomLink>
            </div>
          </motion.div>
        </HoverableSubItem>
      ))}
    </>
  )
}

function Menu({ fields, drawerOpen }: MenuProps) {
  const isDesktopMenu = useMedia('(min-width: 1200px)')
  const isMobileMenu = useMedia('(max-width: 1199px)')
  const [mounted, setMounted] = useState(false)
  const [activeSubmenuIndex, setActiveSubmenuIndex] = useState<number | null>(
    null
  )
  const [hoverIndex, setHoverIndex] = React.useState<number | null>(null)

  useEffect(() => {
    if (drawerOpen === false) {
      setActiveSubmenuIndex(null)
    }
  }, [drawerOpen])

  useEffect(() => {
    setMounted(true)
  }, [])

  const toggleHover = (index: number) => {
    setHoverIndex(index)
  }

  const resetHover = () => {
    setHoverIndex(null)
  }

  const subMenuAnimate = {
    enter: {
      opacity: 1,
      rotateX: 0,
      overflow: 'hidden',
      transition: {
        duration: 0.2,
      },
      display: 'flex',
    },
    exit: {
      opacity: 0,
      rotateX: -15,
      overflow: 'hidden',
      transition: {
        duration: 0.2,
      },
      transitionEnd: {
        display: 'none',
      },
    },
  }

  const activeSubmenuEdge =
    activeSubmenuIndex !== null
      ? fields.header.menuHeader[activeSubmenuIndex]
      : null

  const mobileSubmenuOverlay =
    mounted &&
    isMobileMenu &&
    activeSubmenuIndex !== null &&
    activeSubmenuEdge?.submenu &&
    typeof document !== 'undefined'
      ? createPortal(
          <MobileSubmenuOverlay
            role="dialog"
            aria-modal="true"
            aria-label={
              activeSubmenuEdge.link?.title
                ? `Submenu: ${activeSubmenuEdge.link.title}`
                : 'Submenu'
            }
          >
            <MobileSubmenuBackButton
              type="button"
              onClick={() => setActiveSubmenuIndex(null)}
              aria-label="Terug naar hoofdmenu"
            >
              ← Terug
            </MobileSubmenuBackButton>
            <MobileSubmenuOverlayInner>
              <SubMenu fields={activeSubmenuEdge} />
            </MobileSubmenuOverlayInner>
          </MobileSubmenuOverlay>,
          document.body
        )
      : null

  return (
    <>
      {mobileSubmenuOverlay}
      {fields.header.menuHeader.map((edge: any, index: number) => {
        if (edge.submenu) {
          return (
            <HoverableItem
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="pr-1"
              onMouseEnter={
                isDesktopMenu ? () => toggleHover(index) : undefined
              }
              onMouseLeave={isDesktopMenu ? resetHover : undefined}
            >
              <SubMenuWrapper
                initial="exit"
                animate={hoverIndex === index ? 'enter' : 'exit'}
                variants={subMenuAnimate}
                className="container px-3 py-2 flex-column"
              >
                <div className="row">
                  <div className="position-relative">
                    <SubMenu fields={edge} />
                  </div>
                </div>
              </SubMenuWrapper>
              <LinkContainer className="d-flex justify-content-center justify-content-xl-start align-items-center position-relative text-uppercase">
                <StyledCustomLink to={edge.link.url} className="py-3">
                  {edge.link.title}
                </StyledCustomLink>
                {isMobileMenu && (
                  <button
                    type="button"
                    className="pl-5 border-0 bg-transparent p-0 d-flex align-items-center justify-content-center"
                    aria-expanded={activeSubmenuIndex === index}
                    aria-label="Open submenu"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setActiveSubmenuIndex(index)
                    }}
                  >
                    <img
                      src={ArrowRight}
                      alt=""
                      aria-hidden
                      width={20}
                      height={20}
                    />
                  </button>
                )}
              </LinkContainer>
            </HoverableItem>
          )
        }

        return (
          // eslint-disable-next-line react/no-array-index-key
          <Item key={index} className="pr-1">
            <StyledCustomLink to={edge.link.url} className="py-3">
              {edge.link.title}
            </StyledCustomLink>
          </Item>
        )
      })}
    </>
  )
}

export default Header
